import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, fileUrl, brl } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, X, Power } from 'lucide-react';

const EMPTY = { name: '', category: 'Geral', sale_price: 0, cost: 0, stock: 0, min_stock: 0, barcode: '', photo_path: null };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(null); // product or EMPTY
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const load = useCallback(async () => setProducts((await api.get('/products')).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEdit({ ...EMPTY });
  const openEdit = (p) => setEdit({ ...p });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEdit((e) => ({ ...e, photo_path: r.data.path }));
      toast.success('Foto enviada');
    } catch (e) { toast.error('Falha no upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const payload = { ...edit };
    ['sale_price', 'cost', 'stock', 'min_stock'].forEach((k) => { payload[k] = Number(payload[k]) || 0; });
    if (!payload.name.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      if (edit.id) {
        const { id, active, deleted, created_at, created_by, stock, ...upd } = payload;
        await api.put(`/products/${id}`, upd);
        toast.success('Produto atualizado');
      } else {
        await api.post('/products', payload);
        toast.success('Produto criado');
      }
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const toggleActive = async (p) => {
    if (p.stock <= 0 && !p.active) { toast.error('Sem estoque - adicione entrada primeiro'); return; }
    await api.put(`/products/${p.id}`, { active: !p.active });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}" definitivamente?\n\nSe houver vendas registradas, será arquivado (histórico preservado).`)) return;
    try {
      const r = await api.delete(`/products/${p.id}`);
      toast.success(r.data.archived ? 'Produto arquivado (histórico preservado)' : 'Produto excluído');
      load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="p-4 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-black">Produtos</h2>
        <button onClick={openNew} data-testid="product-new" className="h-10 px-4 rounded-xl bg-orange-500 text-white font-bold flex items-center gap-1 active:scale-95"><Plus size={18} /> Novo</button>
      </div>

      <div className="space-y-2">
        {products.map((p) => {
          const disabled = !p.active || p.stock <= 0;
          return (
            <div key={p.id} data-testid={`product-row-${p.id}`}
                 className={`bg-white rounded-2xl border border-slate-200 p-3 flex items-center gap-3 ${disabled ? 'opacity-50 grayscale' : ''}`}>
              <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                {p.photo_path ? <img src={fileUrl(p.photo_path)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full grid place-items-center text-slate-400 font-black">{p.name.slice(0,1)}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{p.name}</div>
                <div className="text-xs text-slate-500">{p.category} · {brl(p.sale_price)} · Custo {brl(p.cost)}</div>
                <div className="text-xs mt-0.5">
                  <span className={p.stock <= 0 ? 'text-red-600 font-bold' : p.stock <= (p.min_stock||0) ? 'text-amber-600 font-bold' : 'text-slate-600'}>
                    {p.stock <= 0 ? 'Sem estoque' : `Estoque: ${p.stock}`}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center active:scale-95"><Edit size={16}/></button>
                <button onClick={() => toggleActive(p)} data-testid={`product-toggle-${p.id}`} className={`w-9 h-9 rounded-lg grid place-items-center active:scale-95 ${p.active ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'}`}><Power size={16}/></button>
                <button onClick={() => remove(p)} data-testid={`product-delete-${p.id}`} className="w-9 h-9 rounded-lg bg-red-50 text-red-600 grid place-items-center active:scale-95"><Trash2 size={16}/></button>
              </div>
            </div>
          );
        })}
        {products.length === 0 && <div className="text-center text-slate-500 py-10">Nenhum produto cadastrado</div>}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={() => setEdit(null)} data-testid="product-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <div className="font-black">{edit.id ? 'Editar produto' : 'Novo produto'}</div>
              <button onClick={() => setEdit(null)}><X/></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-32 rounded-2xl bg-slate-100 overflow-hidden grid place-items-center">
                  {edit.photo_path ? <img src={fileUrl(edit.photo_path)} alt="" className="w-full h-full object-cover"/> : <span className="text-slate-400 text-sm">Sem foto</span>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="product-photo-input"/>
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="product-photo-btn"
                        className="h-10 px-4 rounded-xl bg-slate-100 font-bold text-sm flex items-center gap-2 active:scale-95 disabled:opacity-50">
                  <Upload size={16}/> {uploading ? 'Enviando...' : (edit.photo_path ? 'Trocar foto' : 'Selecionar foto')}
                </button>
              </div>
              <Field label="Nome" v={edit.name} onC={(v) => setEdit({...edit, name: v})} testid="product-name"/>
              <Field label="Categoria" v={edit.category} onC={(v) => setEdit({...edit, category: v})} testid="product-category"/>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Preço venda (R$)" type="number" v={edit.sale_price} onC={(v) => setEdit({...edit, sale_price: v})} testid="product-price"/>
                <Field label="Custo (R$)" type="number" v={edit.cost} onC={(v) => setEdit({...edit, cost: v})} testid="product-cost"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {!edit.id && <Field label="Estoque inicial" type="number" v={edit.stock} onC={(v) => setEdit({...edit, stock: v})} testid="product-stock"/>}
                <Field label="Estoque mínimo" type="number" v={edit.min_stock} onC={(v) => setEdit({...edit, min_stock: v})} testid="product-min-stock"/>
              </div>
              <Field label="Código de barras" v={edit.barcode} onC={(v) => setEdit({...edit, barcode: v})} testid="product-barcode"/>
              <button onClick={save} data-testid="product-save" className="w-full h-12 rounded-xl bg-orange-500 text-white font-black active:scale-[0.98]">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, onC, type = 'text', testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <input type={type} value={v ?? ''} onChange={(e) => onC(e.target.value)} data-testid={testid}
             className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"/>
    </div>
  );
}
